import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActionNeededTypes } from '@core/enums/action-needed-type.enum';
import { LeaveAdminDashboardTableFilters } from '@core/models/leave-admin/leave-admin-dashboard-table-filters.model';
import { ConstantsService, LayoutService } from '@core/services';
import { FormControl, FormGroup } from '@angular/forms';
import { isNumber } from '@microsoft/applicationinsights-core-js';
import { SelectionOption, StatusChips } from '@core/models';
import { LeaveAdminDashboardFormService } from '@core/services/leave-admin-dashboard-form.service';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LeaveAdminDashboardForm } from '@core/models/leave-admin/leave-admin-dashboard-form.model';

@Component({
    selector: 'app-leave-admin-dashboard-table-filters',
    templateUrl: './leave-admin-dashboard-table-filters.component.html',
    styleUrls: ['./leave-admin-dashboard-table-filters.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LeaveAdminDashboardTableFiltersComponent implements OnInit {
  static readonly componentName = 'LeaveAdminDashboardTableFiltersComponent';
  
  @Output() showHiddenTab = new EventEmitter<boolean>();

  public actionNeededOptions: SelectionOption[];
  public chipStyles: StatusChips;
  public actionNeededTypes = ActionNeededTypes;
  public locationOptions: SelectionOption[];

  public position = position;
  public onDragEnded = onDragEnded;

  private destroy$: Subject<void> = new Subject<void>();

  public isMobile$: Observable<boolean>;

  get company(): FormControl<number> {
    return this.formService.companyControl;
  }

  get location(): FormControl<SelectionOption> {
    return this.formService.locationControl;
  }

  get leaveType(): FormControl<string> {
    return this.formService.leaveTypeControl;
  }

  get stage(): FormControl<number> {
    return this.formService.stageControl;
  }

  get admin(): FormControl<number> {
    return this.formService.adminControl;
  }

  get actionNeeded(): FormControl<number> {
    return this.formService.actionNeededControl;
  }

  get showHidden(): FormControl<boolean> {
    return this.formService.hiddenControl;
  }

  get form(): FormGroup<LeaveAdminDashboardForm> {
    return this.formService.form;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { 
      tableFilterOptions: LeaveAdminDashboardTableFilters;
      sysText: any;
      showHideCaseFeatures: boolean;
    },
    private layoutService: LayoutService,
    private constants: ConstantsService,
    private formService: LeaveAdminDashboardFormService,
    private dialogRef: MatDialogRef<LeaveAdminDashboardTableFiltersComponent>
  ) { }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    this.chipStyles = this.constants.STAT_CHIP_STYLES; 
    this.actionNeededOptions = this.buildActionNeededOptions();
    this.locationOptions = this.data.tableFilterOptions.locations;

    if (this.data.tableFilterOptions.showCompanies) {
      this.company.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (res) {
            const selectedCompName = this.data.tableFilterOptions.companies
              .find(co => co.id === res)?.description;

            // clear selected location if it doesn't belong to selected company 
            if (this.location.value != null) {
              const selectedLocComp = this.data.tableFilterOptions.locations
                .find(lo => lo.id === this.location.value.id)?.optionInfo;
              if (selectedLocComp != selectedCompName) {
                this.location.setValue(null);
              }
            }
  
            // filter loaction options to selected company
            this.locationOptions = this.data.tableFilterOptions.locations.filter(opt => {
              return opt.optionInfo === selectedCompName;
            });
          } else {
            this.locationOptions = this.data.tableFilterOptions.locations;
            this.location.setValue(null);
          }
      });

      // location changes auto patch company id 
      this.location.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (res) {
            const companyId = this.data.tableFilterOptions.companies
              .find(co => co.description === res.optionInfo)?.id as number;
            this.company.setValue(companyId, {emitEvent: false});
          }
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onApply():void {
    //This may look odd, but it's checking for a "blank" admin value (neither null or undefined).
    //If it's blank, we want to set it to null.
    if (!this.admin.value) {
      this.admin.patchValue(null);
    }

    if (this.data.showHideCaseFeatures) {
      this.showHiddenTab.emit(this.showHidden.value == true);
    }

    this.dialogRef.close(true)
  }

  public onClearFilters(): void {
    this.company.patchValue(null);
    this.location.patchValue(null);
    this.leaveType.patchValue(null);
    this.stage.patchValue(null);
    this.admin.patchValue(null);
    this.actionNeeded.patchValue(null);

    if (this.data.showHideCaseFeatures) {
      this.showHidden.patchValue(null);
    }
  }

  public compareWith(o1: SelectionOption, o2: SelectionOption): boolean {
    return o1?.id == o2?.id;
  }

  private buildActionNeededOptions(): SelectionOption[] {
    const actionNeededIds = Object.values(this.actionNeededTypes).filter(v => isNumber(v));
    let actionNeededOptions: SelectionOption[] = [];

    actionNeededIds.forEach(id => {
      const desc: string = this.data.sysText['actionNeededOptions_' + id];
      const option: SelectionOption = {
        id: id,
        description: desc
      };
      actionNeededOptions.push(option);
    });

    return actionNeededOptions;
  }
}